import * as React from 'react';
import Route from 'modules/router';

import CheckAuthentication, { USER_OBTAINED_NO_SESSION, USER_OBTAINED_SESSION } from 'modules/check-authentication';
import ErrorScreen from '../screens/error';
import InviteeScreen from '../screens/invitee';
import InvitationScreen from '../screens/invitation';
import PlatformRegisterScreen from '../screens/platform-register';
import StatusCheckContainer from '../modules/status-check';
import FriendReferralsInvitee from '../modules/friends-referral-invitee';
import { LandingPage } from '../modules/landing-page';
import { DashboardPage } from '../modules/dashboard-page';

import {
    FRIENDS_REFERRAL_FAIL_CAMPAIGN_STATUS,
    FRIENDS_REFERRAL_GOT_STATUS_INFO,
    FRIENDS_REFERRAL_SET_ERROR,
    FRIENDS_REFERRAL_SUCCESS_CAMPAIGN_STATUS,
    GetFriendsReferralInfo,
    CheckFriendsReferralInfo
} from '../modules/friends-referral';
import { SET_LOGIN_SESSION, LOG_OUT_DONE } from 'modules/login/login.constants';

import {
    STEP_CHECK_AUTHENTICATION,
    STEP_CHECK_INVITATION,
    STEP_CHECK_STATUS,
    STEP_ERROR,
    STEP_ERROR_NOT_ELIGIBLE,
    STEP_ERROR_NOT_LINKED,
    STEP_ERROR_STATUS,
    STEP_ERROR_MAINTENANCE,
    STEP_GET_FRIENDS_REFERRAL_INFO,
    STEP_INVITATION_PAGE,
    STEP_INVITEE_PAGE,
    STEP_LOGIN,
    STEP_PLATFORM_REGISTER,
    STEP_REFERRAL_DASHBOARD,
    STEP_UBISERVICES_REQUEST_ERROR,
    STEP_CHECK_FRIENDS_REFERRAL_INFO,
    STEP_END_OF_OPERATION
} from './router.constants';
import {
    FRIENDS_REFERRAL_INVITATION_ACCEPTED,
    FRIENDS_REFERRAL_GOT_INVITATION_INFO,
    FRIENDS_REFERRAL_SET_INELIGIBLE_ERROR,
    FRIENDS_REFERRAL_SET_REGISTERED_REFERRER_INFO,
    FRIENDS_REFERRAL_SET_STAT_SERVICE_ERROR,
    FRIENDS_REFERRAL_NOT_LINKED,
    IFriendReferralsInfo,
    FRIENDS_REFERRAL_SET_CAMPAIGN_INFO,
    FRIENDS_REFERRAL_MAINTENANCE_CAMPAIGN_STATUS
} from '../modules/friends-referral/friends-referral-store';
import { IAction } from 'types/action';
import { ERROR_TYPE } from '../modules/error-page';
import { OPERATION_ENDING_STATUS } from 'modules/end-operation/end-operation.constants';
import EndOperationScreen from '../screens/end-operation';

const commonTransitions = {
    [LOG_OUT_DONE]: { STEP_LOGIN },
    [FRIENDS_REFERRAL_SET_ERROR]: { STEP_ERROR }
};

export const Router: React.FC = () => {
    return (
        <>
            <Route
                is={STEP_CHECK_STATUS}
                transitions={{
                    ...commonTransitions,
                    [FRIENDS_REFERRAL_SUCCESS_CAMPAIGN_STATUS]: { STEP_CHECK_AUTHENTICATION },
                    [FRIENDS_REFERRAL_MAINTENANCE_CAMPAIGN_STATUS]: { STEP_ERROR_MAINTENANCE },
                    [FRIENDS_REFERRAL_FAIL_CAMPAIGN_STATUS]: { STEP_ERROR_STATUS }
                }}
                render={<StatusCheckContainer />}
            />
            <Route
                is={STEP_CHECK_AUTHENTICATION}
                transitions={{
                    ...commonTransitions,
                    [USER_OBTAINED_SESSION]: { STEP_LOGIN },
                    [USER_OBTAINED_NO_SESSION]: { STEP_LOGIN }
                }}
                render={<CheckAuthentication />}
            />
            <Route
                is={STEP_LOGIN}
                transitions={{
                    ...commonTransitions,
                    [SET_LOGIN_SESSION]: { STEP_GET_FRIENDS_REFERRAL_INFO }
                }}
                render={<LandingPage />}
            />
            <Route
                is={STEP_GET_FRIENDS_REFERRAL_INFO}
                transitions={{
                    ...commonTransitions,
                    [FRIENDS_REFERRAL_SET_CAMPAIGN_INFO]: { STEP_END_OF_OPERATION }
                }}
                render={<GetFriendsReferralInfo />}
            />
            <Route
                is={STEP_END_OF_OPERATION}
                transitions={{
                    [OPERATION_ENDING_STATUS]: {
                        [STEP_CHECK_FRIENDS_REFERRAL_INFO]: action => action.payload.isValid
                    }
                }}
                render={<EndOperationScreen/>}
            />
            <Route
                is={STEP_CHECK_FRIENDS_REFERRAL_INFO}
                transitions={{
                    ...commonTransitions,
                    [FRIENDS_REFERRAL_GOT_STATUS_INFO]: {
                        [STEP_REFERRAL_DASHBOARD]: (action: IAction<IFriendReferralsInfo>) =>
                            action.payload!.hasOwnershipOnAnyPlatform && action.payload!.isReferrer,
                        [STEP_PLATFORM_REGISTER]: (action: IAction<IFriendReferralsInfo>) =>
                            action.payload!.hasOwnershipOnAnyPlatform && !action.payload!.isReferrer,
                        [STEP_CHECK_INVITATION]: (action: IAction<IFriendReferralsInfo>) =>
                            !action.payload!.hasOwnershipOnAnyPlatform,
                    }
                }}
                render={<CheckFriendsReferralInfo />}
            />
            <Route
                is={STEP_CHECK_INVITATION}
                transitions={{
                    ...commonTransitions,
                    [FRIENDS_REFERRAL_GOT_INVITATION_INFO]: { STEP_INVITATION_PAGE },
                    [FRIENDS_REFERRAL_INVITATION_ACCEPTED]: { STEP_INVITEE_PAGE },
                    [FRIENDS_REFERRAL_SET_INELIGIBLE_ERROR]: { STEP_ERROR_NOT_ELIGIBLE },
                    [FRIENDS_REFERRAL_NOT_LINKED]: { STEP_ERROR_NOT_LINKED },
                    [FRIENDS_REFERRAL_SET_STAT_SERVICE_ERROR]: { STEP_UBISERVICES_REQUEST_ERROR },
                    [FRIENDS_REFERRAL_SET_ERROR]: { STEP_UBISERVICES_REQUEST_ERROR }
                }}
                render={<FriendReferralsInvitee />}
            />
            <Route
                is={STEP_INVITEE_PAGE}
                transitions={{
                    ...commonTransitions,
                    [FRIENDS_REFERRAL_SET_INELIGIBLE_ERROR]: { STEP_ERROR_NOT_ELIGIBLE },
                    [FRIENDS_REFERRAL_SET_STAT_SERVICE_ERROR]: { STEP_UBISERVICES_REQUEST_ERROR }
                }}
                render={<InviteeScreen />}
            />
            <Route
                is={STEP_INVITATION_PAGE}
                transitions={{
                    ...commonTransitions,
                    [FRIENDS_REFERRAL_INVITATION_ACCEPTED]: { STEP_INVITEE_PAGE },
                    [FRIENDS_REFERRAL_SET_INELIGIBLE_ERROR]: { STEP_ERROR_NOT_ELIGIBLE },
                    [FRIENDS_REFERRAL_NOT_LINKED]: { STEP_ERROR_NOT_LINKED },
                    [FRIENDS_REFERRAL_SET_STAT_SERVICE_ERROR]: { STEP_UBISERVICES_REQUEST_ERROR },
                    [FRIENDS_REFERRAL_SET_ERROR]: { STEP_UBISERVICES_REQUEST_ERROR }
                }}
                render={<InvitationScreen />}
            />
            <Route
                is={STEP_REFERRAL_DASHBOARD}
                transitions={{ ...commonTransitions }}
                render={<DashboardPage />}
            />
            <Route
                is={STEP_PLATFORM_REGISTER}
                transitions={{
                    ...commonTransitions,
                    [FRIENDS_REFERRAL_SET_REGISTERED_REFERRER_INFO]: { STEP_REFERRAL_DASHBOARD }
                }}
                render={<PlatformRegisterScreen />}
            />
            <Route
                is={STEP_ERROR}
                transitions={{ ...commonTransitions }}
                render={<ErrorScreen />}
            />
            <Route
                is={STEP_ERROR_NOT_ELIGIBLE}
                transitions={{ ...commonTransitions }}
                render={<ErrorScreen type={ERROR_TYPE.INELIGIBLE} />}
            />
            <Route
                is={STEP_ERROR_NOT_LINKED}
                transitions={commonTransitions}
                render={<ErrorScreen type={ERROR_TYPE.PLATFORM_NOT_LINKED} />}
            />
            <Route
                is={STEP_ERROR_STATUS}
                transitions={commonTransitions}
                render={<ErrorScreen type={ERROR_TYPE.CAMPAIGN_STATUS_FAIL} />}
            />
            <Route
                is={STEP_ERROR_MAINTENANCE}
                transitions={commonTransitions}
                render={<ErrorScreen type={ERROR_TYPE.CAMPAIGN_STATUS_MAINTENANCE} />}
            />
            <Route
                is={STEP_UBISERVICES_REQUEST_ERROR}
                transitions={commonTransitions}
                render={<ErrorScreen type={ERROR_TYPE.UBISERVICES_REQUEST_FAIL} />}
            />
        </>
    );
};

Router.displayName = 'Router';

export default Router;
